import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { combine } from 'src/app/shared/utilities/route';
import { FacilityContractRules } from 'src/app/facility-contracts/shared/models/facility-contract-rules.model';
import { FacilityCopyContractRules } from 'src/app/facility-contracts/facilities/models/facility-copy-contract-rules.model';
import { FacilityContractInsurance } from 'src/app/facility-contracts/shared/models/facility-contract-insurance.model';
import { FacilityContractTypes } from 'src/app/facility-contracts/shared/models/facility-contract-types.model';
import { FacilityContractFlags } from 'src/app/facility-contracts/shared/models/facility-contract-flags.model';
import { LookupIntranet } from 'src/app/facility-contracts/shared/models/lookup-intranet.model';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { FacilityCopyContractFlags } from 'src/app/facility-contracts/facilities/models/facility-copy-contract-flags.model';
import { FacilityCopyContractTypes } from 'src/app/facility-contracts/facilities/models/facility-copy-contract-types.model';

@Injectable({
    providedIn: 'root'
})
export class ContractStatusService {
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    // Contract Rules
    getContractRules(id: number, isSystem: boolean): Observable<FacilityContractRules> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/rules`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/rules`;
        return this.http.get<FacilityContractRules>(url);
    }

    updateContractRules(id: number, data: FacilityContractRules, isSystem: boolean): Observable<FacilityContractRules> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/rules`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/rules`;
        return this.http.put(url, data) as Observable<FacilityContractRules>;
    }

    copyContractRules(id: number, rules: FacilityCopyContractRules): Observable<unknown> {
        const url = `${this.coreApiPath}/facility-contracts/facility/${id}/rules/copy`;
        return this.http.post(url, rules);
    }

    // Contract Insurance
    getContractInsurance(id: number, isSystem: boolean): Observable<FacilityContractInsurance> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/insurance`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/insurance`;
        return this.http.get<FacilityContractInsurance>(url);
    }

    updateContractInsurance(
        id: number,
        data: FacilityContractInsurance,
        isSystem: boolean
    ): Observable<FacilityContractInsurance> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/insurance`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/insurance`;
        return this.http.put(url, data) as Observable<FacilityContractInsurance>;
    }

    // Contract Types
    getContractTypes(id: number, isSystem: boolean): Observable<FacilityContractTypes> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/types`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/types`;
        return this.http.get<FacilityContractTypes>(url);
    }

    updateContractTypes(id: number, data: FacilityContractTypes, isSystem: boolean): Observable<FacilityContractTypes> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/types`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/types`;
        return this.http.put(url, data) as Observable<FacilityContractTypes>;
    }

    // Contract Flags
    getContractFlags(id: number, isSystem: boolean): Observable<FacilityContractFlags> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/flags`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/flags`;
        return this.http.get<FacilityContractFlags>(url);
    }

    updateContractFlags(id: number, data: FacilityContractFlags, isSystem: boolean): Observable<FacilityContractFlags> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/flags`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/flags`;
        return this.http.put(url, data) as Observable<FacilityContractFlags>;
    }

    copyContractFlags(id: number, flags: FacilityCopyContractFlags, isSystem: boolean): Observable<unknown> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/flags/copy`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/flags/copy`;
        return this.http.post(url, flags);
    }

    copyContractTypes(id: number, types: FacilityCopyContractTypes, isSystem: boolean): Observable<unknown> {
        const url = isSystem
            ? `${this.coreApiPath}/facility-contracts/system/${id}/types/copy`
            : `${this.coreApiPath}/facility-contracts/facility/${id}/types/copy`;
        return this.http.post(url, types);
    }

    getBrands(): Observable<LookupIntranet[]> {
        const url = `${this.coreApiPath}/facility-contracts/system/brands`;
        return this.http.get<LookupIntranet[]>(url).pipe(
            tap((brands) => {
                const noBrand = [{ id: '0', name: '' }];
                return noBrand.concat(brands);
            })
        );
    }
}
